/**
 * Canonical date/time helpers for the tenant/site-local wire format.
 *
 * Two categories live here:
 *   1. `nowInTimeZone` — the one intentional real `Intl` zone conversion that stays on the
 *      frontend (browser clock instant → site-zone wall clock, for new-record form defaults
 *      and calendar "today"/max-date affordances).
 *   2. The display formatters (`formatTenantDateTime`, `formatTenantTime`, `formatTimelineTooltip`,
 *      `formatTimelineDotDate`, `formatCalendarDate`, `formatMissedDateDisplay`) — these NEVER re-convert
 *      timezones. They receive an already-local wall-clock string from the backend and only apply 12h/24h
 *      presentation.
 */

const WALL_CLOCK_RE = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
const TIMELINE_RE = /(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2})?)?/

interface WallClockParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

/**
 * Renders a Date instant's fields in `timeZone` as a naive "YYYY-MM-DDTHH:mm" wall-clock string.
 * Used for form defaults, datetime-local values, and client-side schedule previews.
 */
export const toWallClockString = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const partValue = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${partValue('year')}-${partValue('month')}-${partValue('day')}T${partValue('hour')}:${partValue('minute')}`
}

/**
 * Converts a wall-clock value ("YYYY-MM-DDTHH:mm", interpreted in `timeZone`) to a UTC ISO
 * instant. Two-pass offset correction keeps DST transitions correct. Used only for client-side
 * instant comparisons (e.g. TaskLogForm window checks) — write paths convert on the server.
 */
export const wallClockToUtcIso = (localValue: string, timeZone: string): string => {
  const asUtcMs = new Date(localValue + 'Z').getTime()

  const offsetAt = (utcMs: number): number => {
    const rendered = toWallClockString(new Date(utcMs), timeZone)
    return new Date(rendered + 'Z').getTime() - utcMs
  }

  const firstGuessMs = asUtcMs - offsetAt(asUtcMs)
  const correctedMs = asUtcMs - offsetAt(firstGuessMs)
  return new Date(correctedMs).toISOString()
}

/**
 * Returns the current wall-clock time in `timeZone`, represented as a local `Date` object
 * (i.e. `Date#getFullYear/getMonth/getDate/getHours/getMinutes` reflect `timeZone`, not the
 * browser's timezone). Intended for calendar/time-picker widgets that compare `Date` fields
 * directly rather than treating the value as a real UTC instant.
 */
export const nowInTimeZone = (timeZone: string): Date => {
  const [datePart, timePart] = toWallClockString(new Date(), timeZone).split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour, minute] = timePart.split(':').map(Number)
  return new Date(year, month - 1, day, hour, minute)
}

// Shared parsing core for the no-conversion display formatters. Reads the wall-clock
// components directly from the string instead of routing through `new Date(iso)`, which would
// re-interpret the value in the browser's zone and shift it.
const parseWallClock = (localString: string): WallClockParts | null => {
  const match = localString.match(WALL_CLOCK_RE)
  if (!match) return null

  const [, yearStr, monthStr, dayStr, hourStr, minuteStr] = match
  return {
    year: Number(yearStr),
    month: Number(monthStr),
    day: Number(dayStr),
    hour: Number(hourStr),
    minute: Number(minuteStr),
  }
}

const formatWallClockTime = (
  hour: number,
  minute: number,
  tenantTimeFormat: number | null | undefined,
): string => {
  const formattedMinute = minute.toString().padStart(2, '0')

  if (tenantTimeFormat === 12) {
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${formattedMinute} ${period}`
  }

  return `${hour.toString().padStart(2, '0')}:${formattedMinute}`
}

/**
 * Formats an already-local wall-clock string ("YYYY-MM-DDTHH:mm", no zone suffix) as a
 * date + time. Applies only the tenant's 12h/24h preference — never a timezone conversion.
 */
export const formatTenantDateTime = (
  localString: string,
  tenantTimeFormat: number | null | undefined,
): string => {
  const parts = parseWallClock(localString)
  if (!parts) return localString

  const monthLabel = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleString(
    undefined,
    { month: 'short', timeZone: 'UTC' },
  )

  return `${monthLabel} ${parts.day}, ${parts.year}, ${formatWallClockTime(parts.hour, parts.minute, tenantTimeFormat)}`
}

/**
 * `formatTenantDateTime` followed by the backend-computed site zone abbreviation
 * (e.g. "Oct 12, 2026, 3:45 PM EDT"). The abbreviation is only appended — never used for
 * a timezone conversion.
 */
export const formatTenantDateTimeWithZone = (
  localString: string,
  zoneAbbreviation: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
): string => {
  const formatted = formatTenantDateTime(localString, tenantTimeFormat)
  return zoneAbbreviation ? `${formatted} ${zoneAbbreviation}` : formatted
}

/**
 * Short zone abbreviation (EDT / EST / …) for an already-local wall-clock string interpreted
 * in `timeZone`. Converts the wall clock to its UTC instant first so DST follows that
 * timestamp — not "now" (REBUILD-598). Returns '' when zone/wall-clock is missing or invalid.
 */
export const siteZoneAbbreviation = (
  wallClockString: string | null | undefined,
  timeZone: string | null | undefined,
): string => {
  if (!wallClockString || !timeZone) return ''

  const wallClockMinute = wallClockString.slice(0, 16)
  if (!WALL_CLOCK_RE.test(wallClockMinute)) return ''

  try {
    const utcInstant = new Date(wallClockToUtcIso(wallClockMinute, timeZone))
    if (Number.isNaN(utcInstant.getTime())) return ''

    const abbreviation = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(utcInstant)
      .find((part) => part.type === 'timeZoneName')?.value

    return abbreviation ?? ''
  } catch {
    return ''
  }
}

/**
 * THE shared "date, time, zone" display (REBUILD-611 / REBUILD-613): `Mmm D, YYYY, <time> <zone>`
 * (e.g. "Oct 12, 2026, 3:45 PM EDT"). `siteTimeZone` is the IANA zone that owns the site-local
 * wall clock; the abbreviation follows that instant's DST and is only appended — the zone is
 * omitted when it is unknown, never replaced by the browser zone.
 */
export const formatSiteDateTime = (
  localString: string,
  siteTimeZone: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
): string =>
  formatTenantDateTimeWithZone(
    localString,
    siteZoneAbbreviation(localString, siteTimeZone),
    tenantTimeFormat,
  )

/** Appends the site zone abbreviation of `wallClockString` to an already-formatted time label. */
export const appendSiteZone = (
  formatted: string,
  wallClockString: string | null | undefined,
  siteTimeZone: string | null | undefined,
): string => {
  const abbreviation = siteZoneAbbreviation(wallClockString, siteTimeZone)
  return abbreviation ? `${formatted} ${abbreviation}` : formatted
}

/**
 * Display for a recorded missed / completed instant on the NC surfaces (REBUILD-597):
 * `Mmm D, YYYY, <time> <zone>` (e.g. "Oct 20, 2026, 3:15 PM EDT"), tenant 12h/24h aware.
 * `wallClockString` is the site-local wall clock and `siteTimeZone` the IANA zone that owns
 * it; the abbreviation follows that instant's DST, never the browser zone. Falls back to the
 * date-only `Mmm D, YYYY` when no time is known, and omits the zone when it is unknown.
 */
export const formatRecordedInstantWithZone = (
  wallClockString: string | null | undefined,
  dateOnlyFallback: string | null | undefined,
  siteTimeZone: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
): string => {
  if (wallClockString && parseWallClock(wallClockString)) {
    return formatTenantDateTimeWithZone(
      wallClockString,
      siteZoneAbbreviation(wallClockString, siteTimeZone),
      tenantTimeFormat,
    )
  }

  const dateOnly = (wallClockString || dateOnlyFallback || '').slice(0, 10)
  const parsed = parseCalendarDate(dateOnly)
  if (!parsed) return dateOnly
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Browser-local wall-clock string from a Date's local getters (no IANA zone conversion). */
export const toBrowserWallClockString = (date: Date = new Date()): string => {
  const pad = (digits: number) => digits.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Formats a real UTC/ISO instant as `Mmm D, YYYY, <time> <zone>` in `siteTimeZone`
 * (REBUILD-611 / REBUILD-613) — e.g. the Frequencies due-point preview. The instant is
 * converted to the site wall clock first, then shown through `formatSiteDateTime`, so the
 * result never depends on the browser zone. Returns the input untouched when it is not a
 * valid instant, and shows no zone when the site zone is unknown.
 */
export const formatInstantInSiteZone = (
  isoOrDate: string | Date,
  siteTimeZone: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
): string => {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (Number.isNaN(date.getTime())) {
    return typeof isoOrDate === 'string' ? isoOrDate : ''
  }

  const wallClock = siteTimeZone
    ? toWallClockString(date, siteTimeZone)
    : toBrowserWallClockString(date)
  return formatSiteDateTime(wallClock, siteTimeZone, tenantTimeFormat)
}

/**
 * Formats the time-of-day portion of an already-local wall-clock string. Applies only the
 * tenant's 12h/24h preference — never a timezone conversion.
 */
export const formatTenantTime = (
  localString: string,
  tenantTimeFormat: number | null | undefined,
): string => {
  const parts = parseWallClock(localString)
  if (!parts) return localString

  return formatWallClockTime(parts.hour, parts.minute, tenantTimeFormat)
}

/**
 * Formats a date string as MM/DD/YYYY with optional time in tenant 12/24h format.
 * Used for timeline-dot tooltips per UAT requirement: date is always MM/DD/YYYY,
 * time (when present) respects tenant_time_format (HH:mm or hh:mm A), no seconds.
 */
export const formatTimelineTooltip = (
  dateStr: string,
  tenantTimeFormat: number | null | undefined,
  siteTimeZone?: string | null,
): string => {
  const match = dateStr.match(TIMELINE_RE)
  if (!match) return dateStr

  const [, , monthStr, dayStr, hourStr, minuteStr] = match
  const datePart = `${monthStr}/${dayStr}/${match[1]}`

  if (!hourStr || !minuteStr) return datePart

  const withTime = `${datePart} ${formatWallClockTime(Number(hourStr), Number(minuteStr), tenantTimeFormat)}`
  return appendSiteZone(
    withTime,
    `${match[1]}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}`,
    siteTimeZone,
  )
}

/**
 * Formats a site-local wall-clock or date-only string as MM/DD for visible timeline dot/column
 * labels (legacy formatDateCard → formatDate parity). Parses the YYYY-MM-DD prefix only — no year,
 * no timezone re-conversion. Tooltips use `formatTimelineTooltip` (MM/DD/YYYY + time).
 */
export const formatTimelineDotDate = (localWallClockOrDateString: string): string => {
  const match = localWallClockOrDateString.match(TIMELINE_RE)
  if (!match) return localWallClockOrDateString

  const [, , monthStr, dayStr] = match
  return `${monthStr}/${dayStr}`
}

/**
 * Parses a YYYY-MM-DD (or longer string whose first 10 chars are a calendar day) into a
 * local `Date` at midnight. Returns null when the day components are missing/invalid.
 */
export const parseCalendarDate = (dateOnlyString: string): Date | null => {
  if (!dateOnlyString) return null
  const [year, month, day] = dateOnlyString.slice(0, 10).split('-').map(Number)
  if (!year || Number.isNaN(month) || Number.isNaN(day)) return null
  return new Date(year, month - 1, day)
}

/**
 * Formats a YYYY-MM-DD date-only string as MM/DD/YYYY. Parses the year/month/day components
 * locally so UTC-midnight shifts never move the displayed day. The single canonical date-only
 * formatter.
 *
 * The format is pinned rather than taken from the browser locale: legacy always rendered
 * MM/DD/YYYY, and deferring to `toLocaleDateString(undefined)` turned every date into DD/MM/YYYY
 * for anyone whose machine is set to a non-US locale — the same day, read as a different one
 * (REBUILD-365). Matches the MM/DD/YYYY rule `formatTimelineTooltip` already follows.
 */
export const formatCalendarDate = (dateOnlyString: string): string => {
  const parsed = parseCalendarDate(dateOnlyString)
  if (!parsed) return dateOnlyString
  return parsed.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

/**
 * THE standardized human-readable display for a missed / completed instant: `MM/DD/YYYY`
 * with an optional `, HH:mm` / `, h:mm A` suffix (tenant 12h/24h aware). This is the single
 * source of truth every NC surface routes through — the NC card, the Resolve default view,
 * the Resolve editor's masked prefill, and the calendar occurrence header — so one instant
 * reads the same everywhere (REBUILD-474 AC1/AC4). No timezone re-conversion: the date comes
 * from a date-only / wall-clock prefix and the time from a site-local wall-clock string.
 */
export const formatMissedDateDisplay = (
  dateOnlyString: string,
  missedTimeLocal: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
  siteTimeZone?: string | null,
): string => {
  const datePart = formatCalendarDate(dateOnlyString)
  if (!missedTimeLocal) return datePart

  return appendSiteZone(
    `${datePart}, ${formatTenantTime(missedTimeLocal, tenantTimeFormat)}`,
    missedTimeLocal,
    siteTimeZone,
  )
}

/**
 * Formats a single site-local wall-clock string (`YYYY-MM-DDTHH:mm`, no zone suffix) through
 * the standardized missed-instant pattern (`MM/DD/YYYY, HH:mm` / `, h:mm A`). Convenience
 * wrapper over `formatMissedDateDisplay` for callers that hold one combined wall-clock value
 * (e.g. the NC Resolve `completedAt`) rather than separate date-only + time props — so they
 * do not fall back to a different formatter (the dual-pattern bug in REBUILD-474). No
 * timezone re-conversion.
 */
export const formatWallClockDateTimeDisplay = (
  wallClockString: string | null | undefined,
  tenantTimeFormat: number | null | undefined,
  siteTimeZone?: string | null,
): string => {
  if (!wallClockString) return ''
  const parts = parseWallClock(wallClockString)
  // No parseable time component (date-only input): render just the date.
  if (!parts) return formatCalendarDate(wallClockString)

  const dateOnly = wallClockString.slice(0, 10)
  return formatMissedDateDisplay(dateOnly, wallClockString, tenantTimeFormat, siteTimeZone)
}

/**
 * Formats a YYYY-MM or YYYY-MM-DD string as a long month + year label (e.g. "June 2024").
 * Parses year/month locally so UTC midnight never shifts the calendar month.
 */
export const formatMonthYearLabel = (yearMonthOrDate: string): string => {
  const [year, month] = yearMonthOrDate.slice(0, 7).split('-').map(Number)
  if (!year || !month) return yearMonthOrDate
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Formats a local `Date`'s calendar fields as YYYY-MM-DD (no timezone conversion).
 * Used by date pickers and month-navigation math that work in calendar days.
 */
export const toCalendarDateString = (date: Date): string => {
  return toBrowserWallClockString(date).slice(0, 10)
}

/**
 * Returns the 1st of the calendar month `direction` months away from `dateOrMonth`
 * (YYYY-MM or YYYY-MM-DD). Only the year/month components are read, so a day-of-month that
 * does not exist in the target month can never overflow into the month after it (stepping
 * forward from the 31st must land on the next month, not skip it).
 */
export const shiftMonth = (dateOrMonth: string, direction: -1 | 1): string => {
  const [year, month] = dateOrMonth.slice(0, 7).split('-').map(Number)
  return toCalendarDateString(new Date(year, month - 1 + direction, 1))
}

/** Returns the last calendar day of the month containing `dateOrMonth` as YYYY-MM-DD. */
export const endOfMonth = (dateOrMonth: string): string => {
  // month from the ISO string is 1-indexed; new Date(y, month, 0) is the last day of it.
  const [year, month] = dateOrMonth.slice(0, 7).split('-').map(Number)
  return toCalendarDateString(new Date(year, month, 0))
}

/** Returns the 1st of the month containing `siteToday`, falling back to the browser's today. */
export const currentMonthStart = (siteToday?: string): string => {
  if (siteToday) {
    const [year, month] = siteToday.slice(0, 7).split('-').map(Number)
    return toCalendarDateString(new Date(year, month - 1, 1))
  }
  const today = new Date()
  return toCalendarDateString(new Date(today.getFullYear(), today.getMonth(), 1))
}

/**
 * Trims a wall-clock / ISO-like string to the `datetime-local` length `YYYY-MM-DDTHH:mm`.
 * Does not convert timezones — callers that still hold a UTC ISO must convert first via
 * `toWallClockString(new Date(iso), timeZone)`.
 */
export const toDatetimeLocalValue = (wallClockOrIso: string | null | undefined): string => {
  if (!wallClockOrIso) return ''
  const match = wallClockOrIso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/)
  return match ? match[1] : ''
}
